import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import EmptyEventsState from "../Events/EmptyEventsState";
import messages from "../../assets/locale/messages";
import * as companyPortalEventsApi from "../../network/apis/companyPortalEvents";
import { getEventsRequest } from "../../store/Events/actions";
import { eventStatuses } from "../../utils/Constants";
import { showErrorMsg } from "../../utils/Helpers";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import { getPortalApiErrorMessage } from "../../utils/companyPortal/eventApi";
import {
  flattenCompanyPortalEventsList,
  readPortalEventsListMeta,
} from "../../utils/companyPortal/portalEventsList";
import {
  UICard,
  UIPaginationBar,
  UITable,
  UITabs,
} from "../ui";
import { getClientProfileEventsColumns } from "./clientProfileEventsColumns";

const PORTALS = {
  admin: "admin",
  company: "company",
};

const ClientProfileEvents = ({ clientId, filterByClientId, portal }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isCompanyPortal = portal === PORTALS.company;

  const lang = useSelector((state) => state.locale.lang);
  const adminLoading = useSelector((state) => state.loader);
  const adminList = useSelector((state) => state.events.list);
  const adminMeta = useSelector((state) => state.events.meta);
  const { events, shared } = messages[lang];
  const labels = events.listEvents;

  const [activeStatus, setActiveStatus] = useState(eventStatuses.ongoing);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [companyList, setCompanyList] = useState([]);
  const [companyMeta, setCompanyMeta] = useState({});
  const [companyLoading, setCompanyLoading] = useState(false);

  const tabs = useMemo(
    () => [
      { value: eventStatuses.ongoing, label: labels.eventTabs.ongoing },
      { value: eventStatuses.upcoming, label: labels.eventTabs.upcoming },
      { value: eventStatuses.past, label: labels.eventTabs.previous },
      { value: eventStatuses.pending, label: labels.eventTabs.pending },
      { value: eventStatuses.cancelled, label: labels.eventTabs.cancelled },
    ],
    [labels.eventTabs]
  );

  useEffect(() => {
    if (!clientId || isCompanyPortal) return;

    dispatch(
      getEventsRequest({
        page,
        items: pageSize,
        status: activeStatus,
        client_id: clientId,
      })
    );
  }, [dispatch, clientId, page, pageSize, activeStatus, isCompanyPortal]);

  useEffect(() => {
    if (!isCompanyPortal || !filterByClientId) return undefined;

    let cancelled = false;
    setCompanyLoading(true);

    companyPortalEventsApi
      .getCompanyPortalEventsList({
        page,
        items: pageSize,
        status: activeStatus,
        client_id: filterByClientId,
      })
      .then((response) => {
        if (cancelled) return;
        setCompanyList(flattenCompanyPortalEventsList(response));
        setCompanyMeta(readPortalEventsListMeta(response));
      })
      .catch((error) => {
        if (cancelled) return;
        setCompanyList([]);
        setCompanyMeta({});
        showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
      })
      .finally(() => {
        if (!cancelled) setCompanyLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    filterByClientId,
    page,
    pageSize,
    activeStatus,
    isCompanyPortal,
    shared.generalErrMsg,
  ]);

  useEffect(() => {
    setPage(1);
  }, [activeStatus, pageSize, clientId, filterByClientId]);

  const columns = useMemo(
    () => getClientProfileEventsColumns({ labels, events, lang }),
    [events, labels, lang]
  );

  const list = isCompanyPortal ? companyList : adminList;
  const meta = isCompanyPortal ? companyMeta : adminMeta;
  const isLoading = isCompanyPortal ? companyLoading : adminLoading;

  const rows = useMemo(
    () =>
      (list || []).map((row) => ({
        ...row,
        id: row.id,
      })),
    [list]
  );

  const currentPage = meta?.currentPage || page;
  const paginationFrom =
    meta?.count > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(currentPage * pageSize, meta?.count || 0);

  const eventViewPath = isCompanyPortal
    ? ROUTE_PATHS.companyViewEvent
    : ROUTE_PATHS.viewEvent;

  const renderTableBody = () => {
    if (!isLoading && !rows.length) {
      return (
        <EmptyEventsState
          title={labels.emptyTitle}
          description={labels.noDataDescription}
        />
      );
    }

    return (
      <>
        <UITable
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          contentSized
          rowHeight={72}
          hideFooter
          className="client-portal-profile-page__events-table"
          onRowClick={({ row }) =>
            history.push(eventViewPath.replace(":id", row.id))
          }
        />
        {(meta?.count || 0) > 0 && (
          <UIPaginationBar
            currentPage={currentPage}
            pages={meta?.pages || 1}
            pageSize={pageSize}
            rangeTemplate={labels.showingEventsRange}
            rangeFrom={paginationFrom}
            rangeTo={paginationTo}
            rangeTotal={meta?.count || 0}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={setPage}
            onPageSizeChange={(nextSize) => {
              setPageSize(nextSize);
              setPage(1);
            }}
            className="client-portal-profile-page__pagination"
          />
        )}
      </>
    );
  };

  return (
    <div className="client-portal-profile-page__events">
      <UITabs
        tabs={tabs}
        value={activeStatus}
        onChange={setActiveStatus}
        variant="scrollable"
        scrollButtons={false}
        rootSx={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-16)",
        }}
      >
        <UICard>{renderTableBody()}</UICard>
      </UITabs>
    </div>
  );
};

ClientProfileEvents.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  filterByClientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  portal: PropTypes.oneOf(Object.values(PORTALS)).isRequired,
};

ClientProfileEvents.defaultProps = {
  clientId: null,
  filterByClientId: null,
};

export default ClientProfileEvents;
